import { inject, Injectable } from '@angular/core';
import { CARDNET_ENV, CARDNET_FORM_ID, CARDNET_KEY } from './cardnet.tokens';
import { PwCheckoutEvent, PwCheckoutInstance } from './pw-checkout';

/**
 * What actually happened inside the Cardnet window.
 *
 * `dismissed` means the window closed without Cardnet ever emitting
 * `tokenCreated`: the person cancelled, or the card was rejected. Either way
 * no card was saved, and the caller has to say so instead of pretending the
 * step succeeded.
 */
export type CaptureResult =
  | { status: 'created'; token: string | null }
  | { status: 'dismissed' };

@Injectable({ providedIn: 'root' })
export class CardnetService {
  readonly env = inject(CARDNET_ENV);
  readonly key = inject(CARDNET_KEY);
  readonly formId = inject(CARDNET_FORM_ID);

  /** Always reads from window at call time — avoids stale capture at injection. */
  get #pwCheckout(): PwCheckoutInstance | undefined {
    return (window as Window & { PWCheckout?: PwCheckoutInstance })['PWCheckout'];
  }

  /**
   * Opens the Cardnet window and resolves once it closes, reporting whether a
   * card was actually tokenized.
   *
   * This used to resolve on `closed` alone, without checking anything. A
   * rejected card and a saved card were indistinguishable to the caller, so
   * the screen simply went back to how it was and the person tried again —
   * eight people ended up with the same card stored between two and five
   * times. `tokenCreated` was already declared in the event type and nothing
   * ever listened to it.
   *
   * Both events are bound before the window opens. Cardnet fires
   * `tokenCreated` first and `closed` right after, so we record the token and
   * still wait for `closed`: resolving early would hand control back while
   * the window is still on screen.
   */
  async capture(customerUniqueId: string): Promise<CaptureResult> {
    const checkout = await this.#waitForCheckout();

    const captureUrl =
      `${this.env}/Capture/?key=${encodeURIComponent(this.key)}` +
      `&session_id=${encodeURIComponent(customerUniqueId)}`;

    await checkout.SetProperties({
      currency: 'DOP',
      autoSubmit: 'false',
      empty: 'false',
      form_id: this.formId,
    });

    const result = new Promise<CaptureResult>((resolve) => {
      let token: string | null = null;
      let tokenCreated = false;
      let settled = false;

      this.bind('tokenCreated', (...args: unknown[]) => {
        tokenCreated = true;
        token = this.#readToken(args);
      });

      this.bind('closed', () => {
        if (settled) return;
        settled = true;
        this.unbind('tokenCreated');
        this.unbind('closed');
        resolve(tokenCreated ? { status: 'created', token } : { status: 'dismissed' });
      });
    });

    await checkout.OpenIframeCustom(captureUrl, customerUniqueId);

    return result;
  }

  bind(event: PwCheckoutEvent, fn: (...args: unknown[]) => unknown) {
    this.#pwCheckout?.Bind(event, fn);
  }

  unbind(event: PwCheckoutEvent) {
    this.#pwCheckout?.Unbind(event);
  }

  /**
   * Cardnet does not document the `tokenCreated` payload, so take the token
   * only if it is plainly there. A missing token is not a failure: the event
   * firing is the signal that matters, and the card is read back from the
   * payment account afterwards.
   */
  #readToken(args: unknown[]): string | null {
    for (const arg of args) {
      if (typeof arg === 'string' && arg.length > 0) return arg;
      if (arg && typeof arg === 'object') {
        const bag = arg as Record<string, unknown>;
        for (const key of ['token', 'Token', 'cardToken', 'tokenId']) {
          const value = bag[key];
          if (typeof value === 'string' && value.length > 0) return value;
        }
      }
    }
    return null;
  }

  /** Polls window.PWCheckout until ready (max 5 s). */
  #waitForCheckout(): Promise<PwCheckoutInstance> {
    if (this.#pwCheckout?.SetProperties) return Promise.resolve(this.#pwCheckout);

    return new Promise((resolve, reject) => {
      let attempts = 0;
      const poll = setInterval(() => {
        if (this.#pwCheckout?.SetProperties) {
          clearInterval(poll);
          resolve(this.#pwCheckout);
        } else if (++attempts >= 50) {
          clearInterval(poll);
          reject(new Error('CardNET checkout library failed to load'));
        }
      }, 100);
    });
  }
}
